import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';

// ── Column definition ──────────────────────────────────────────────────────────

export interface SearchColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface MasterSearchModalProps<T extends { id: string }> {
  isOpen:    boolean;
  onClose:   () => void;
  title:     string;
  columns:   SearchColumn<T>[];
  fetchData: (
    search:   string,
    page:     number,
    pageSize: number,
  ) => Promise<{ items: T[]; totalCount: number }>;
  onSelect:  (item: T) => void;
}

const PAGE_SIZE = 10;

// ── Component ─────────────────────────────────────────────────────────────────

export function MasterSearchModal<T extends { id: string }>({
  isOpen,
  onClose,
  title,
  columns,
  fetchData,
  onSelect,
}: MasterSearchModalProps<T>) {
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [items,      setItems]      = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState<T | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const load = useCallback(async (q: string, pg: number) => {
    setLoading(true);
    try {
      const result = await fetchData(q, pg, PAGE_SIZE);
      setItems(result.items);
      setTotalCount(result.totalCount);
      setSelected(null);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  // Load on open, or when search / page changes
  useEffect(() => {
    if (!isOpen) return;
    load(search, page);
  }, [isOpen, search, page, load]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setPage(1);
      setSelected(null);
    }
  }, [isOpen]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const handleRowClick  = (item: T) => setSelected(item);
  const handleRowDouble = (item: T) => { onSelect(item); onClose(); };

  const handleSelect = () => {
    if (selected) { onSelect(selected); onClose(); }
  };

  const getCellValue = (row: T, col: SearchColumn<T>): ReactNode => {
    if (col.render) return col.render(row);
    const val = (row as Record<string, unknown>)[col.key];
    return val == null ? '—' : String(val);
  };

  if (!isOpen) return null;

  return (
    /* Overlay */
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Dialog */}
      <div className="relative w-full max-w-3xl mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search…"
              autoFocus
              className={cn(
                'w-full h-9 pl-9 pr-3 text-sm rounded-md border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
              )}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm text-gray-400">
              No records found.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/60">
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={cn(
                        'text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-600',
                        col.className,
                      )}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const isSelected = selected?.id === item.id;
                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleRowClick(item)}
                      onDoubleClick={() => handleRowDouble(item)}
                      className={cn(
                        'border-b border-gray-100 dark:border-gray-700/50 cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-brand-50 dark:bg-brand-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/40',
                      )}
                    >
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={cn('px-4 py-2.5 text-gray-800 dark:text-gray-200', col.className)}
                        >
                          {getCellValue(item, col)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer — pagination + actions */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
          {/* Pagination */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
              <span className="ml-2 text-gray-400">({totalCount} records)</span>
            </span>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-8 rounded text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSelect}
              disabled={!selected}
              className="px-4 h-8 rounded text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
